/**
 * In-memory probe-reading ring buffer for sparkline rendering.
 *
 * Sparkline history is intentionally ephemeral: it lives only in the
 * browser tab, survives no refresh, and never hits the backend. Real
 * historical data is a separate (future) milestone.
 *
 * Buffer key
 * ----------
 * We key by `${probeType}::${name}`. Apex display names are not strictly
 * unique across modules (rare but possible — e.g. two `Tmp` probes on two
 * Trident pods), and they can be renamed in Fusion. Compounding with
 * `probeType` reduces collision risk; the trade-off is that renaming a
 * probe restarts its history. We accept that until the Apex surfaces a
 * stable per-probe UUID.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ApexProbeSnapshot } from './types';

/** Maximum samples retained per probe. The dashboard sparkline shows ≤60. */
export const MAX_HISTORY_SAMPLES = 60;

export type ProbeHistoryMap = Map<string, number[]>;

/** Compose the buffer key used everywhere in this module. */
export function historyKey(probe: Pick<ApexProbeSnapshot, 'probeType' | 'name'>): string {
  return `${probe.probeType}::${probe.name}`;
}

export interface UseProbeHistory {
  /** Push the latest readings; one entry per probe, in current order. */
  record(probes: readonly ApexProbeSnapshot[], fetchedAtMs: number | undefined): void;
  /** Read the buffer for a given probe. Returns an empty array if unknown. */
  series(probe: Pick<ApexProbeSnapshot, 'probeType' | 'name'>): readonly number[];
  /** Test/utility hook: drop every buffered sample. */
  reset(): void;
}

/**
 * React hook that tracks per-probe value history.
 *
 * The internal store lives in a `useRef` (no re-render on every push); a
 * separate `version` counter forces a re-render when callers care about
 * the new value (e.g. the dashboard's sparklines). We dedupe per-poll
 * pushes by `fetchedAtMs` so back-to-back renders of the same agent
 * snapshot don't double-count samples.
 */
export function useProbeHistory(): UseProbeHistory {
  const storeRef = useRef<ProbeHistoryMap>(new Map());
  const lastFetchedAtRef = useRef<number | undefined>(undefined);
  const [, setVersion] = useState(0);

  const record = useCallback(
    (probes: readonly ApexProbeSnapshot[], fetchedAtMs: number | undefined): void => {
      // Skip duplicate snapshots — the dashboard polls every 2s but the
      // Apex bridge only refreshes every 5s, so half of our renders will
      // see the same `fetchedAtMs` as the previous tick.
      if (fetchedAtMs !== undefined && fetchedAtMs === lastFetchedAtRef.current) return;
      lastFetchedAtRef.current = fetchedAtMs;

      const store = storeRef.current;
      let changed = false;
      for (const p of probes) {
        if (!Number.isFinite(p.value)) continue;
        const key = historyKey(p);
        const arr = store.get(key) ?? [];
        arr.push(p.value);
        if (arr.length > MAX_HISTORY_SAMPLES) arr.splice(0, arr.length - MAX_HISTORY_SAMPLES);
        store.set(key, arr);
        changed = true;
      }
      if (changed) setVersion((v) => v + 1);
    },
    [],
  );

  const series = useCallback(
    (probe: Pick<ApexProbeSnapshot, 'probeType' | 'name'>): readonly number[] => {
      return storeRef.current.get(historyKey(probe)) ?? [];
    },
    [],
  );

  const reset = useCallback((): void => {
    storeRef.current.clear();
    lastFetchedAtRef.current = undefined;
    setVersion((v) => v + 1);
  }, []);

  // Clear on unmount so a HMR doesn't leave stale buffers in memory.
  useEffect(() => () => storeRef.current.clear(), []);

  return { record, series, reset };
}